import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { api, eur } from "../lib/api";
import { LightArt } from "../components/LightArt";
import { Navigation } from "../components/Navigation";
import { useCart } from "../context/CartContext";

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { count } = useCart();

  useEffect(() => {
    document.title = "Shop · Die Lichtmalerin";
    window.scrollTo(0, 0);
    api
      .get("/products")
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#090909]" data-testid="shop-page">
      <Navigation />

      <section className="pt-32 md:pt-40 pb-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <span className="gold-divider">Shop · Lichtwerke</span>
              <h1
                data-testid="shop-heading"
                className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#E5E5E5] mt-8"
              >
                Lichtwerke zum <span className="italic gold-text">Mitnehmen</span>
              </h1>
              <p className="mt-6 max-w-xl font-sans font-light text-base text-[#E5E5E5]/70 leading-relaxed">
                Handgefertigte Fine-Art-Prints, signiert und limitiert.
                Jedes Bild eine einzelne Belichtung — jedes Original.
              </p>
            </div>

            <div className="flex items-center gap-6">
              <Link
                to="/widerruf"
                data-testid="shop-widerruf-link"
                className="btn-gold inline-flex items-center gap-2 px-6 py-3 font-sans font-light tracking-widest text-xs uppercase"
              >
                Widerruf
              </Link>
              <Link
                to="/cart"
                data-testid="shop-cart-link"
                className="relative inline-flex items-center gap-3 text-[#D4AF37] hover:text-[#FDE047] transition-colors"
              >
                <ShoppingBag size={20} strokeWidth={1.4} />
                <span className="text-xs uppercase tracking-[0.3em] font-sans font-light">
                  Warenkorb ({count})
                </span>
              </Link>
            </div>
          </div>

          {loading ? (
            <p className="text-[#A1A1AA] font-sans" data-testid="shop-loading">
              Lade Werke…
            </p>
          ) : products.length === 0 ? (
            <p className="text-[#A1A1AA] font-sans" data-testid="shop-empty">
              Aktuell keine Werke verfügbar.
            </p>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10"
              data-testid="shop-grid"
            >
              {products.map((p, idx) => (
                <motion.article
                  key={p.id}
                  data-testid={`shop-product-${p.id}`}
                  initial={{ opacity: 0, filter: "blur(20px) brightness(0.2)" }}
                  whileInView={{
                    opacity: 1,
                    filter: "blur(0px) brightness(1)",
                  }}
                  transition={{ duration: 1.4, delay: (idx % 3) * 0.1 }}
                  viewport={{ once: true, margin: "-40px" }}
                  className="group flex flex-col border border-[#D4AF37]/12 bg-[#0b0b0b] overflow-hidden"
                >
                  <Link
                    to={`/shop/${p.id}`}
                    className="block relative aspect-[4/5] overflow-hidden"
                  >
                    {p.image_base64 ? (
                      <img
                        src={p.image_base64}
                        alt={p.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                      />
                    ) : (
                      <LightArt variant={((idx + 1) % 6) + 1} />
                    )}
                    <div
                      className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                      style={{
                        boxShadow: "inset 0 0 60px rgba(212,175,55,0.35)",
                      }}
                    />
                  </Link>

                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-serif italic text-xl text-[#E5E5E5]">
                      {p.title}
                    </h3>
                    {p.subtitle && (
                      <p className="mt-1 text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]/70 font-sans">
                        {p.subtitle}
                      </p>
                    )}
                    <p className="mt-3 font-sans font-light text-sm text-[#E5E5E5]/70 line-clamp-3">
                      {p.description}
                    </p>

                    <div className="mt-6 flex items-center justify-between">
                      <p
                        className="font-serif text-2xl text-[#D4AF37]"
                        data-testid={`shop-product-price-${p.id}`}
                      >
                        {eur(p.price_eur)}
                      </p>
                      <Link
                        to={`/shop/${p.id}`}
                        data-testid={`shop-product-open-${p.id}`}
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-sans font-light text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                      >
                        Ansehen
                        <ArrowRight size={14} strokeWidth={1.4} />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer with impressum */}
      <footer className="max-w-7xl mx-auto px-6 lg:px-12 py-10 mt-16 border-t border-[#D4AF37]/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-sans font-light text-[#A1A1AA] uppercase tracking-[0.35em]">
        <p>© {new Date().getFullYear()} · Nicole Meinhold</p>
        <Link to="/impressum" className="hover:text-[#D4AF37] transition-colors">
          Impressum
        </Link>
      </footer>
    </div>
  );
}
